import _ from 'underscore';
import { useCallback, useMemo } from 'react';
import { useDataSelection } from '@bingads-webui-react/data-selection';

import { useAggregatedData } from '../context';
import { convertDataSelectionToArrayForm } from '../utils';
import { useTreeData } from './use-tree-data';

export const buildCheckedItemsMap = (dataSelection: any[], items: any[], itemsMap: Map<any, any>) => {
  const checkedItemsMap = new Map();

  let itemValues = dataSelection.map(({ value }) => value);

  // Data selection only contains leaf nodes, so we can mark them as checked directly
  itemValues.forEach((value) => checkedItemsMap.set(value, true));

  while (!_.isEmpty(itemValues)) {
    const ancestors = new Set();

    itemValues.forEach((value) => {
      const itemEntry = itemsMap.get(value);

      if (!itemEntry || _.isEmpty(itemEntry.ancestor)) { return; }

      const parentIndex = itemEntry.ancestor[itemEntry.ancestor.length - 1];
      const parentValue = items[parentIndex]?.value;

      if (parentValue) {
        ancestors.add(parentValue);
      }
    });

    const ancestorsArray = [...ancestors];

    ancestorsArray.forEach((value) => {
      const ancestorEntry = itemsMap.get(value);

      const allChildrenChecked = _.all(ancestorEntry.children, (childIndex) => {
        const childValue = items[childIndex]?.value;
        return checkedItemsMap.has(childValue) && checkedItemsMap.get(childValue) === true;
      });

      checkedItemsMap.set(value, allChildrenChecked ? true : 'mixed');
    });

    itemValues = ancestorsArray;
  }

  return checkedItemsMap;
};

export const useRenderingData = ({
  mode,
  showSelectedOnly,
  customFilter,

}) => {
  const { hasPagesMissing, isLoading, getNextMissingPage } = useAggregatedData();
  const {
    dataSelection,
    openItems,
    setDataSelection,
    setOpenItems,
  } = useDataSelection();

  const {
    fullItems, items, itemsMap, isList,
  } = useTreeData({
    mode,
    showSelectedOnly,
    customFilter,
  });

  const checkedItems = useMemo(
    () => buildCheckedItemsMap(dataSelection, fullItems, itemsMap),
    [dataSelection, fullItems, itemsMap],
  );

  const onChange = useCallback((newValues) => {
    const newSelectionValuesAsArray = convertDataSelectionToArrayForm(newValues);

    const newSelection = _.chain(newSelectionValuesAsArray)
      .map((value) => {
        const itemFromSelection = dataSelection.find((item) => item.value === value);
        const itemFromItems = itemsMap.get(value);

        return ({
          value,
          content: itemFromSelection?.content ?? itemFromItems?.content,
          data: itemFromSelection?.data ?? itemFromItems?.data,
        });
      })
      .reject((item) => itemsMap.has(item.value) && !_.isEmpty(itemsMap.get(item.value).children))
      .value();

    setDataSelection(newSelection);
  }, [dataSelection, itemsMap, setDataSelection]);

  const onReachEnd = useCallback(() => {
    if (isLoading || !hasPagesMissing) { return; }

    getNextMissingPage();
  }, [isLoading, hasPagesMissing, getNextMissingPage]);

  return {
    items, itemsMap, checkedItems, openItems, setOpenItems, isList, onReachEnd, onChange, dataSelection, setDataSelection,
  };
};
